import { FC, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CircularProgress } from '@mui/material';
import { T_KEYS } from '../../constants';

// Saudi Arabia flag SVG
const SaudiFlag = () => (
  <svg width="29" height="20" viewBox="0 0 29 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="29" height="20" rx="2" fill="#006C35"/>
    <text x="14.5" y="12" textAnchor="middle" fill="white" fontSize="6" fontFamily="Arial">🇸🇦</text>
  </svg>
);

// Chevron down icon
const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 6L8 10L12 6" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface GathernPhoneStepProps {
  onSubmit: (phoneNumber: string) => void;
  isLoading: boolean;
  error?: string;
}

const GathernPhoneStep: FC<GathernPhoneStepProps> = ({
  onSubmit,
  isLoading,
  error,
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [phoneNumber, setPhoneNumber] = useState('');

  // Validate phone number (Saudi format: 05xxxxxxxx - 10 digits starting with 05)
  const isValidPhone = phoneNumber.length === 10 && phoneNumber.startsWith('05');

  const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits
    const value = e.target.value.replace(/\D/g, '');
    // Limit to 10 digits
    setPhoneNumber(value.slice(0, 10));
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    // Remove non-digits and any country code prefix (+966, 00966, 966)
    let cleaned = pastedText.replace(/\D/g, '');
    // Remove Saudi country code if present
    if (cleaned.startsWith('966')) {
      cleaned = cleaned.slice(3);
    }
    // Add leading 0 if starts with 5 (user pasted without leading 0)
    if (cleaned.startsWith('5') && !cleaned.startsWith('0')) {
      cleaned = '0' + cleaned;
    }
    // Limit to 10 digits
    setPhoneNumber(cleaned.slice(0, 10));
  }, []);

  const handleSubmit = useCallback(() => {
    if (isValidPhone && !isLoading) {
      // Remove leading 0 and add country code: 05xxxxxxxx -> +9665xxxxxxxx
      onSubmit(`+966${phoneNumber.slice(1)}`);
    }
  }, [isValidPhone, isLoading, phoneNumber, onSubmit]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  }, [handleSubmit]);

  return (
    <Container>
      <Header>
        <Title>{t(T_KEYS.GATHERN_LOGIN_TITLE)}</Title>
        <Description>
          {t(T_KEYS.GATHERN_LOGIN_DESC)}
        </Description>
      </Header>

      <InputSection>
        <InputLabel>{t(T_KEYS.PHONE_NUMBER)}</InputLabel>
        <PhoneInputWrapper $hasError={!!error}>

          <PhoneInput
            type="tel"
            placeholder={t(T_KEYS.ENTER_PHONE_NUMBER)}
            value={phoneNumber}
            onChange={handlePhoneChange}
            onPaste={handlePaste}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
          />
          <CountrySelector>
            {/* <ChevronDown /> */}
            <CountryCode>+966</CountryCode>
            <FlagWrapper>
              <SaudiFlag />
            </FlagWrapper>
          </CountrySelector>
        </PhoneInputWrapper>
        {error && <ErrorText>{error}</ErrorText>}
      </InputSection>

      <SubmitButton 
        onClick={handleSubmit}
        disabled={!isValidPhone || isLoading}
        $disabled={!isValidPhone || isLoading}
      >
        {isLoading ? (
          <CircularProgress size={20} sx={{ color: 'white' }} />
        ) : (
          t(T_KEYS.CONTINUE)
        )}
      </SubmitButton>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  background: white;
  border-radius: 24px;
  padding: 40px 32px;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h2`
  font-weight: 700;
  font-size: 28px;
  line-height: 36px;
  color: #262626;
  margin: 0;
`;

const Description = styled.p`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #666666;
  margin: 0;
`;

const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InputLabel = styled.label`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #262626;
`;

const PhoneInputWrapper = styled.div<{ $hasError?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8f8f8;
  border: 1px solid ${({ $hasError }) => $hasError ? '#d32f2f' : 'transparent'};
  border-radius: 16px;
  padding: 12px 16px;
  height: 48px;
  &:focus-within {
    border-color: ${({ $hasError }) => $hasError ? '#d32f2f' : '#5b2ff9'};
  }
`;

const PhoneInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 16px;
  line-height: 24px;
  color: #262626;
  outline: none;

  &::placeholder {
    color: #666666;
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const CountrySelector = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`;

const CountryCode = styled.span`
  font-size: 16px;
  line-height: 24px;
  color: #1f1f1f;
`;

const FlagWrapper = styled.div`
  width: 29px;
  height: 20px;
  border-radius: 2px;
  overflow: hidden;
`;

const ErrorText = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: #d32f2f;
`;

const SubmitButton = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  height: 56px;
  background: ${({ $disabled }) => $disabled ? '#dddddd' : '#5b2ff9'};
  color: ${({ $disabled }) => $disabled ? '#bdbdbd' : 'white'};
  border: none;
  border-radius: 16px;
  font-weight: 600;
  font-size: 18px;
  line-height: 56px;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${({ $disabled }) => $disabled ? '#dddddd' : '#4a25d6'};
  }
`;

export default GathernPhoneStep;
